import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { addWeeks, format, subWeeks } from "date-fns";
import toast from "react-hot-toast";
import { LogOut, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { AppModalFooter, AppModalShell, AppPageHeader, AppTable } from "../components/app-ui.js";
import { Badge, Button, Card, Input } from "../components/legacy-ui.js";
import {
  createCheeseMaturation,
  deleteCheeseMaturation,
  exitCheeseMaturation,
  getCheeseMaturationOptions,
  getCheeseMaturations,
  updateCheeseMaturation,
} from "../utils/api.js";
import { parseEsVeNumber } from "../utils/formatters.js";
import { companyPageTitle } from "../utils/company.js";
import { LoadingState } from "../components/motion.js";

const fmt = (value, decimals = 2) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const dateKey = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return "";
};

const todayKey = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtDate = (value) => {
  const key = dateKey(value);
  if (key.length < 10) return "—";
  const [year, month, day] = key.split("-");
  return `${day}/${month}/${year}`;
};

const fmtDay = (value) => {
  const key = dateKey(value);
  if (key.length < 10) return "";
  const [, month, day] = key.split("-");
  return `${day}/${month}`;
};

const salesWeekStartKey = (value) => {
  const key = dateKey(value);
  const base = /^\d{4}-\d{2}-\d{2}$/.test(key) ? new Date(`${key}T12:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) {
    return todayKey();
  }
  const day = base.getDay();
  let daysSinceMonday = day - 1;
  if (daysSinceMonday < 0) daysSinceMonday += 7;
  base.setDate(base.getDate() - daysSinceMonday);
  return dateKey(base);
};

const salesWeekEndKey = (startKey) => {
  const start = new Date(`${salesWeekStartKey(startKey)}T12:00:00`);
  start.setDate(start.getDate() + 6);
  return dateKey(start);
};

const salesWeekNavLabel = (startKey) => {
  const start = salesWeekStartKey(startKey);
  const end = salesWeekEndKey(start);
  return `Lunes ${fmtDay(start)} — Domingo ${fmtDay(end)}`;
};

const shiftWeekKey = (startKey, delta) => salesWeekStartKey(addWeeks(new Date(`${salesWeekStartKey(startKey)}T12:00:00`), delta));

const oid = (value) => {
  if (value && typeof value === "object") return String(value._id || value.id || "");
  return String(value || "");
};

const productName = (value) => {
  if (value && typeof value === "object") return value.name || "";
  return String(value || "");
};

const weekNavLabel = (data) => {
  if (!data) return "Cargando semanas…";
  const days = data.days || [];
  const start = days[0]?.date || data.startOfWeek;
  const end = days[days.length - 1]?.date || data.endOfWeek;
  const year = dateKey(end).slice(0, 4);
  return `Jueves ${fmtDay(start)} — Miércoles ${fmtDay(end)} ${year}`;
};

const emptyEntry = () => ({
  product: "",
  name: "",
  cava: "Cava principal",
  entryDate: todayKey(),
  initialWeight: "",
  notes: "",
  exitDate: "",
  finalWeight: "",
  dayCounts: {},
  salesWeekStart: salesWeekStartKey(todayKey()),
});

const statusMeta = (status) => {
  if (status === "salido") return { label: "Salido", type: "success" };
  if (status === "despachando" || status === "en_cava") return { label: "Despachando", type: "warning" };
  return { label: "Pendiente", type: "info" };
};

const mermaPreview = (initialRaw, finalRaw) => {
  const initial = parseEsVeNumber(initialRaw);
  const final = parseEsVeNumber(finalRaw);
  if (!initial || initial <= 0 || final == null) return null;
  const mermaKg = initial - final;
  return {
    mermaKg,
    mermaPct: (mermaKg / initial) * 100,
  };
};

const countsFromSources = (row) => {
  const counts = {};
  (row?.sources || []).forEach((src) => {
    const key = dateKey(src.date);
    if (!key) return;
    counts[key] = String((Number(counts[key]) || 0) + (Number(src.cheeseCount) || 0));
  });
  if (!Object.keys(counts).length && row?.sourceDates) {
    row.sourceDates.forEach((key) => {
      counts[dateKey(key)] = String(row.cheeseCount || "");
    });
  }
  return counts;
};

/** Proportional kg for the cheeses being taken from a production day. */
const dayLoadKg = (day, loadCount) => {
  const count = Math.round(Number(loadCount) || 0);
  if (count <= 0) return 0;
  if (day.remainingCount > 0) {
    return (Number(day.remainingKg) || 0) * (count / day.remainingCount);
  }
  if (day.cheeseCount > 0) {
    return (Number(day.kg) || 0) * (count / day.cheeseCount);
  }
  return Number(day.remainingKg) || 0;
};

const MaturationModule = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [week, setWeek] = useState(null);
  const [weekDate, setWeekDate] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showEntry, setShowEntry] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exiting, setExiting] = useState(null);
  const [entryForm, setEntryForm] = useState(emptyEntry());
  const [exitForm, setExitForm] = useState({ exitDate: todayKey(), finalWeight: "" });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCheeseMaturations({
        status: status || undefined,
        search: debouncedSearch || undefined,
      });
      setRows(res.data.data || []);
      setSummary(res.data.summary || null);
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo cargar la maduración", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  }, [status, debouncedSearch]);

  const fetchWeek = useCallback(async () => {
    try {
      const res = await getCheeseMaturationOptions({
        date: format(weekDate, "yyyy-MM-dd"),
        weeks: 2,
        excludeId: editing?._id || undefined,
      });
      setWeek(res.data.data || null);
    } catch (error) {
      console.error(error);
    }
  }, [weekDate, editing]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (showEntry) fetchWeek();
  }, [showEntry, fetchWeek]);

  const products = week?.products || [];
  const days = useMemo(() => week?.days || [], [week?.days]);

  const productDays = useMemo(() => {
    const productId = entryForm.product;
    return days.map((day) => {
      const info = productId
        ? (day.byProduct || []).find((row) => oid(row.product) === productId)
        : null;
      return {
        ...day,
        remainingCount: info ? Number(info.remainingCount) || 0 : 0,
        remainingKg: info ? Number(info.remainingKg) || 0 : 0,
        cheeseCount: info ? Number(info.cheeseCount) || 0 : 0,
        kg: info ? Number(info.kg) || 0 : 0,
      };
    });
  }, [days, entryForm.product]);

  const dayGroups = useMemo(() => {
    const groups = [];
    productDays.forEach((day) => {
      const label = day.weekLabel || "Semana";
      const last = groups[groups.length - 1];
      if (!last || last.label !== label) groups.push({ label, days: [day] });
      else last.days.push(day);
    });
    return groups;
  }, [productDays]);

  const selectedDays = useMemo(() => {
    return productDays
      .map((day) => {
        const count = Math.round(parseEsVeNumber(entryForm.dayCounts[day.date]) || 0);
        return { ...day, loadCount: count };
      })
      .filter((day) => day.loadCount > 0);
  }, [productDays, entryForm.dayCounts]);

  const loadTotals = useMemo(() => {
    let cheeseCount = 0;
    let suggestedKg = 0;
    selectedDays.forEach((day) => {
      cheeseCount += day.loadCount;
      suggestedKg += dayLoadKg(day, day.loadCount);
    });
    return { cheeseCount, suggestedKg };
  }, [selectedDays]);

  const queseraKg = loadTotals.suggestedKg > 0 ? loadTotals.suggestedKg : Number(editing?.queseraKg) || 0;
  const liveCavaKg = parseEsVeNumber(entryForm.initialWeight);
  const liveStatus = entryForm.finalWeight
    ? "salido"
    : liveCavaKg && liveCavaKg > 0
      ? "despachando"
      : "pendiente";

  const liveMerma = mermaPreview(exiting?.initialWeight, exitForm.finalWeight);
  const editMerma = mermaPreview(entryForm.initialWeight, entryForm.finalWeight);

  const resetEntry = () => {
    setShowEntry(false);
    setEditing(null);
    setEntryForm(emptyEntry());
    setWeekDate(new Date());
  };

  const resetExit = () => {
    setShowExit(false);
    setExiting(null);
    setExitForm({ exitDate: todayKey(), finalWeight: "" });
  };

  const handleAddNew = () => {
    setEditing(null);
    setWeekDate(new Date());
    setEntryForm(emptyEntry());
    setShowEntry(true);
  };

  const toggleDay = (day) => {
    setEntryForm((prev) => {
      const current = Math.round(parseEsVeNumber(prev.dayCounts[day.date]) || 0);
      const next = { ...prev.dayCounts };
      if (current > 0) {
        delete next[day.date];
      } else {
        next[day.date] = String(day.remainingCount || "");
      }
      return { ...prev, dayCounts: next };
    });
  };

  const setDayCount = (date, value) => {
    setEntryForm((prev) => ({
      ...prev,
      dayCounts: { ...prev.dayCounts, [date]: value },
    }));
  };

  const handleEdit = (row) => {
    const sourceKeys = [
      ...(row.sources || []).map((src) => dateKey(src.date)),
      ...(row.sourceDates || []),
    ].filter(Boolean).sort();
    const latest = sourceKeys[sourceKeys.length - 1];
    setEditing(row);
    setWeekDate(latest ? new Date(`${latest}T12:00:00`) : new Date());
    setEntryForm({
      product: oid(row.product),
      name: row.name || "",
      cava: row.cava || "Cava principal",
      entryDate: dateKey(row.entryDate) || todayKey(),
      initialWeight: row.initialWeight != null ? String(row.initialWeight) : "",
      notes: row.notes || "",
      exitDate: dateKey(row.exitDate),
      finalWeight: row.finalWeight != null ? String(row.finalWeight) : "",
      salesWeekStart: salesWeekStartKey(row.salesWeekStart || row.entryDate),
      dayCounts: countsFromSources(row),
    });
    setShowEntry(true);
  };

  const handleExit = (row) => {
    setExiting(row);
    setExitForm({
      exitDate: dateKey(row.exitDate) || todayKey(),
      finalWeight: row.finalWeight != null ? String(row.finalWeight) : "",
    });
    setShowExit(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Eliminar el lote "${row.name || productName(row.product)}" de maduración?`)) {
      return;
    }
    try {
      await deleteCheeseMaturation(row._id);
      toast.success("Lote eliminado", { position: "top-center" });
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar", {
        position: "top-center",
      });
    }
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    const initialWeight = parseEsVeNumber(entryForm.initialWeight);
    if (!entryForm.product) {
      toast.error("Selecciona un producto", { position: "top-center" });
      return;
    }
    if (!selectedDays.length) {
      toast.error("Selecciona los días que se van a llevar", { position: "top-center" });
      return;
    }
    for (const day of selectedDays) {
      if (day.loadCount > day.remainingCount) {
        toast.error(
          `${day.label} ${fmtDay(day.date)} solo tiene ${day.remainingCount} queso(s) disponibles`,
          { position: "top-center" },
        );
        return;
      }
    }
    if (!entryForm.entryDate) {
      toast.error("La fecha de entrada es obligatoria", { position: "top-center" });
      return;
    }
    if (initialWeight != null && initialWeight < 0) {
      toast.error("El peso de cava / camión no es válido", { position: "top-center" });
      return;
    }
    const payload = {
      product: entryForm.product,
      name: entryForm.name.trim(),
      cava: entryForm.cava.trim() || "Cava principal",
      entryDate: entryForm.entryDate,
      salesWeekStart: salesWeekStartKey(entryForm.salesWeekStart || entryForm.entryDate),
      initialWeight: initialWeight && initialWeight > 0 ? initialWeight : null,
      notes: entryForm.notes.trim(),
      days: selectedDays.map((day) => ({
        date: day.date,
        cheeseCount: day.loadCount,
      })),
    };
    if (editing?.status === "salido" || entryForm.finalWeight) {
      const finalWeight = parseEsVeNumber(entryForm.finalWeight);
      if (finalWeight == null || finalWeight < 0) {
        toast.error("El peso final es obligatorio para registrar la salida", { position: "top-center" });
        return;
      }
      payload.exitDate = entryForm.exitDate || todayKey();
      payload.finalWeight = finalWeight;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateCheeseMaturation(editing._id, payload);
        toast.success(
          payload.finalWeight != null
            ? "Lote actualizado y salida registrada"
            : payload.initialWeight
              ? "Lote despachando"
              : "Lote actualizado",
          { position: "top-center" },
        );
      } else {
        await createCheeseMaturation(payload);
        toast.success(
          payload.finalWeight != null
            ? "Lote cargado y salida registrada"
            : payload.initialWeight
              ? "Lote despachando"
              : "Lote registrado. Al llenar cava / camión pasará a Despachando.",
          { position: "top-center" },
        );
      }
      resetEntry();
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExit = async (e) => {
    e.preventDefault();
    if (!exiting) return;
    const finalWeight = parseEsVeNumber(exitForm.finalWeight);
    if (finalWeight == null || finalWeight < 0) {
      toast.error("El peso final es obligatorio", { position: "top-center" });
      return;
    }
    if (!exitForm.exitDate) {
      toast.error("La fecha de salida es obligatoria", { position: "top-center" });
      return;
    }
    setSaving(true);
    try {
      await exitCheeseMaturation(exiting._id, {
        exitDate: exitForm.exitDate,
        finalWeight,
      });
      toast.success("Salida registrada. Merma calculada.", { position: "top-center" });
      resetExit();
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo registrar la salida", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  const filters = [
    { id: "", label: "Todos" },
    { id: "pendiente", label: "Pendientes" },
    { id: "despachando", label: "Despachando" },
    { id: "salido", label: "Salidos" },
  ];

  const entryFields = (
    <form id="maturation-entry-form" onSubmit={handleSaveEntry} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setWeekDate((d) => subWeeks(d, 1))}
        >
          {"<"}
        </Button>
        <span className="min-w-[240px] font-medium text-gray-800">{weekNavLabel(week)}</span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setWeekDate((d) => addWeeks(d, 1))}
        >
          {">"}
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => setWeekDate(new Date())}>
          Pasada y actual
        </Button>
      </div>
      <p className="md:col-span-2 text-xs text-gray-500 -mt-2">
        Marca los días: el peso de quesera aparece solo. El de cava es lo que va en el camión. Las ventas se suman con cada factura.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1.5">Producto *</label>
        <select
          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-sm"
          value={entryForm.product}
          onChange={(e) =>
            setEntryForm((prev) => ({ ...prev, product: e.target.value, dayCounts: {} }))
          }
          required
        >
          <option value="">Selecciona…</option>
          {products.map((product) => (
            <option key={product._id} value={product._id}>
              {product.name}
            </option>
          ))}
        </select>
      </div>
      <Input
        label="Lote / nombre"
        value={entryForm.name}
        onChange={(e) => setEntryForm({ ...entryForm, name: e.target.value })}
        placeholder="Ej. Viaje jueves"
      />

      <div className="md:col-span-2 rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-sm font-semibold text-amber-950">
          Días a llevar
        </div>
        {!entryForm.product ? (
          <p className="px-3 py-4 text-sm text-gray-400">Selecciona un producto para ver los días.</p>
        ) : (
          <div>
            {dayGroups.map((group) => (
              <div key={group.label}>
                <div className="px-3 py-1.5 bg-amber-100/70 text-xs font-bold uppercase tracking-wide text-amber-900">
                  {group.label}
                </div>
                <div className="divide-y divide-gray-100">
                  {group.days.map((day) => {
                    const loadCount = Math.round(parseEsVeNumber(entryForm.dayCounts[day.date]) || 0);
                    const selected = loadCount > 0;
                    const disabled = !day.remainingCount && !selected;
                    const takeKg = selected ? dayLoadKg(day, loadCount) : 0;
                    const afterCount = Math.max(0, (day.remainingCount || 0) - loadCount);
                    const afterKg = Math.max(
                      0,
                      (Number(day.remainingKg) || 0) - takeKg,
                    );
                    return (
                      <label
                        key={day.date}
                        className={`flex flex-wrap items-center gap-3 px-3 py-2.5 ${
                          disabled ? "opacity-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selected}
                          disabled={disabled}
                          onChange={() => toggleDay(day)}
                        />
                        <span className="w-36 text-sm font-medium text-gray-900">
                          {day.label} {fmtDay(day.date)}
                        </span>
                        <span className="text-xs text-gray-500 flex-1">
                          {day.cheeseCount
                            ? selected
                              ? `${fmt(day.cheeseCount, 0)} producidos · llevar ${fmt(loadCount, 0)} (${fmt(takeKg)} kg) · quedan ${fmt(afterCount, 0)}${
                                  afterKg || day.remainingKg ? ` · ${fmt(afterKg)} kg` : ""
                                }`
                              : `${fmt(day.cheeseCount, 0)} producidos · restan ${fmt(day.remainingCount, 0)}${
                                  day.remainingKg || day.kg
                                    ? ` · ${fmt(day.remainingKg || day.kg)} kg`
                                    : ""
                                }`
                            : "Sin producción"}
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          disabled={!selected}
                          placeholder="Quesos"
                          className="w-20 px-2 py-1.5 rounded-md border border-gray-300 text-sm text-right disabled:bg-gray-50"
                          value={entryForm.dayCounts[day.date] || ""}
                          onChange={(e) => setDayCount(day.date, e.target.value)}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="px-3 py-2 bg-gray-50 text-sm text-gray-700">
          A cargar:{" "}
          <span className="font-semibold">
            {fmt(loadTotals.cheeseCount, 0)} quesos · {fmt(loadTotals.suggestedKg)} kg
          </span>
        </div>
      </div>

      <Input
        label="Cava"
        value={entryForm.cava}
        onChange={(e) => setEntryForm({ ...entryForm, cava: e.target.value })}
      />
      <Input
        label="Fecha de entrada (viaje) *"
        type="date"
        value={entryForm.entryDate}
        onChange={(e) => setEntryForm({ ...entryForm, entryDate: e.target.value })}
        required
      />

      <div className="md:col-span-2 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
        <p className="text-sm font-semibold text-indigo-950 mb-1">Semana de venta</p>
        <p className="text-xs text-gray-500 mb-2">
          Elige la semana completa (lunes a domingo) en la que se puede vender este lote. Solo esas facturas se suman.
        </p>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setEntryForm((prev) => ({
                ...prev,
                salesWeekStart: shiftWeekKey(prev.salesWeekStart || prev.entryDate, -1),
              }))
            }
          >
            {"<"}
          </Button>
          <span className="min-w-[240px] font-medium text-gray-900">
            {salesWeekNavLabel(entryForm.salesWeekStart || entryForm.entryDate)}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setEntryForm((prev) => ({
                ...prev,
                salesWeekStart: shiftWeekKey(prev.salesWeekStart || prev.entryDate, 1),
              }))
            }
          >
            {">"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setEntryForm((prev) => ({ ...prev, salesWeekStart: salesWeekStartKey(todayKey()) }))
            }
          >
            Esta semana
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setEntryForm((prev) => ({
                ...prev,
                salesWeekStart: salesWeekStartKey(prev.entryDate),
              }))
            }
          >
            Semana de la entrada
          </Button>
        </div>
      </div>

      <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-3">
        <p className="text-sm font-semibold text-amber-950">Tres pesos</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Quesera (kg)
            </label>
            <div className="w-full px-3.5 py-2.5 rounded-lg border border-amber-200 bg-amber-100/80 text-sm font-semibold text-amber-950">
              {fmt(queseraKg)}
            </div>
            <p className="text-xs text-gray-500 mt-1">Sale de los días seleccionados. No se modifica.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Cava / camión (kg)
            </label>
            <input
              inputMode="decimal"
              placeholder="Al llenarlo pasa a Despachando"
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-sm"
              value={entryForm.initialWeight}
              onChange={(e) => setEntryForm({ ...entryForm, initialWeight: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Estado: <span className="font-semibold">{statusMeta(liveStatus).label}</span>
              {liveStatus === "despachando" ? " · el lote queda despachando." : " · pendiente hasta que lo llenes."}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Ventas (kg)
            </label>
            <div className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-900">
              {fmt(editing?.salesKg || 0)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Facturas de {salesWeekNavLabel(entryForm.salesWeekStart || entryForm.entryDate)}.
            </p>
          </div>
        </div>
      </div>
      <div className="md:col-span-2">
        <Input
          label="Notas"
          value={entryForm.notes}
          onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
        />
      </div>
      <p className="md:col-span-2 text-sm font-semibold text-gray-800 pt-1">
        Salida de cava {editing?.status === "salido" ? "*" : "(opcional)"}
      </p>
      <Input
        label={editing?.status === "salido" ? "Fecha de salida *" : "Fecha de salida"}
        type="date"
        value={entryForm.exitDate}
        onChange={(e) => setEntryForm({ ...entryForm, exitDate: e.target.value })}
        required={editing?.status === "salido"}
      />
      <Input
        label={editing?.status === "salido" ? "Peso final (kg) *" : "Peso final (kg)"}
        inputMode="decimal"
        value={entryForm.finalWeight}
        onChange={(e) => setEntryForm({ ...entryForm, finalWeight: e.target.value })}
        placeholder="Si lo llenas, se registra la salida ahora"
        required={editing?.status === "salido"}
      />
      {editMerma && (
        <p className="md:col-span-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Merma registrada: {fmt(editMerma.mermaKg)} kg ({fmt(editMerma.mermaPct)}%)
        </p>
      )}
    </form>
  );

  const exitFields = (
    <form id="maturation-exit-form" onSubmit={handleSaveExit} className="grid grid-cols-1 gap-4">
      {exiting && (
        <div className="rounded-lg border border-gray-200 bg-slate-50 px-3 py-3 text-sm text-gray-700">
          <p className="font-semibold text-gray-900">
            {exiting.name || productName(exiting.product)}
          </p>
          <p className="mt-1">
            Entró el {fmtDate(exiting.entryDate)}
            {exiting.cheeseCount ? ` · ${exiting.cheeseCount} quesos` : ""}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Quesera {fmt(exiting.queseraKg)} kg · Cava {fmt(exiting.cavaKg ?? exiting.initialWeight)} kg ·
            Ventas {fmt(exiting.salesKg)} kg
            {exiting.salesWeekLabel ? ` (${exiting.salesWeekLabel})` : ""}
          </p>
          {exiting.sourceLabel ? (
            <p className="text-xs text-gray-500 mt-1">Días: {exiting.sourceLabel}</p>
          ) : null}
        </div>
      )}
      <Input
        label="Fecha de salida *"
        type="date"
        value={exitForm.exitDate}
        onChange={(e) => setExitForm({ ...exitForm, exitDate: e.target.value })}
        required
      />
      <Input
        label="Peso final (kg) *"
        inputMode="decimal"
        value={exitForm.finalWeight}
        onChange={(e) => setExitForm({ ...exitForm, finalWeight: e.target.value })}
        placeholder="0,00"
        required
      />
      {liveMerma && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
          <p className="font-semibold">Merma por deshidratación</p>
          <p className="mt-1">
            {fmt(liveMerma.mermaKg)} kg · {fmt(liveMerma.mermaPct)}%
          </p>
        </div>
      )}
    </form>
  );

  const actions = (row) => (
    <div className="flex gap-1 justify-end">
      {row.status !== "salido" && row.status !== "pendiente" && (
        <Button variant="ghost" size="sm" title="Registrar salida" onClick={() => handleExit(row)}>
          <LogOut size={16} />
        </Button>
      )}
      <Button variant="ghost" size="sm" title="Editar" onClick={() => handleEdit(row)}>
        <Pencil size={16} />
      </Button>
      <Button variant="ghost" size="sm" title="Eliminar" onClick={() => handleDelete(row)}>
        <Trash2 size={16} />
      </Button>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Maduración")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Gestión de maduración"
            subtitle="Elige la semana de venta del lote. Las facturas de ese lunes a domingo se suman a las ventas."
            actions={
              <Button onClick={handleAddNew}>
                <Plus size={16} /> Entrar a cava
              </Button>
            }
          />

          {summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              <Card className="p-3">
                <p className="text-xs text-gray-500">Peso quesera</p>
                <p className="text-lg font-bold text-gray-900">{fmt(summary.queseraKg)} kg</p>
                <p className="text-xs text-gray-500">producción seleccionada</p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-gray-500">Peso cava / camión</p>
                <p className="text-lg font-bold text-gray-900">{fmt(summary.cavaKg)} kg</p>
                <p className="text-xs text-gray-500">{summary.despachando?.lots || summary.inCava?.lots || 0} lotes despachando</p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-gray-500">Peso ventas</p>
                <p className="text-lg font-bold text-amber-800">{fmt(summary.salesKg)} kg</p>
                <p className="text-xs text-gray-500">semana de venta de cada lote</p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-gray-500">Restante</p>
                <p className="text-lg font-bold text-gray-900">{fmt(summary.remainingKg)} kg</p>
                <p className="text-xs text-gray-500">cava menos ventas</p>
              </Card>
            </div>
          )}

          <div className="flex flex-wrap gap-1 mt-4 border-b border-gray-200">
            {filters.map((item) => (
              <button
                key={item.id || "all"}
                type="button"
                onClick={() => setStatus(item.id)}
                className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                  status === item.id
                    ? "border-amber-700 text-amber-900"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="relative mt-4 mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por lote, producto o cava…"
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm placeholder-gray-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="app-content">
          {loading ? (
            <LoadingState label="Cargando maduración" />
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {rows.map((row) => (
                  <Card key={row._id} className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <h3 className="font-bold text-gray-900">
                          {row.name || productName(row.product)}
                        </h3>
                        <p className="text-sm text-gray-500">{productName(row.product)}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {row.cava || "Cava"} · {fmtDate(row.entryDate)}
                          {row.status === "salido" ? ` → ${fmtDate(row.exitDate)}` : ""}
                        </p>
                        {row.sourceLabel ? (
                          <p className="text-xs text-gray-500 mt-1">Días: {row.sourceLabel}</p>
                        ) : null}
                        <p className="text-sm text-gray-700 mt-2">
                          {fmt(row.cheeseCount, 0)} quesos
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Quesera {fmt(row.queseraKg)} kg · Cava {fmt(row.cavaKg ?? row.initialWeight)} kg
                        </p>
                        <p className="text-sm font-semibold text-amber-800 mt-1">
                          Ventas {fmt(row.salesKg)} kg
                          {row.salesWeekLabel ? ` (${row.salesWeekLabel})` : ""}
                          {" · "}Restan {fmt(row.remainingKg)} kg
                        </p>
                      </div>
                      <Badge type={statusMeta(row.status).type}>
                        {row.statusLabel || statusMeta(row.status).label}
                      </Badge>
                    </div>
                    <div className="flex justify-end mt-3">{actions(row)}</div>
                  </Card>
                ))}
                {rows.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">
                    No hay lotes de maduración registrados
                  </p>
                )}
              </div>

              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "lote", title: "Lote" },
                    { key: "days", title: "Días" },
                    { key: "cava", title: "Cava" },
                    { key: "entry", title: "Entrada" },
                    { key: "count", title: "Quesos", align: "right" },
                    { key: "quesera", title: "Quesera", align: "right" },
                    { key: "cavaKg", title: "Cava / camión", align: "right" },
                    { key: "sales", title: "Ventas (semana)", align: "right" },
                    { key: "remaining", title: "Restante", align: "right" },
                    { key: "status", title: "Estado", align: "center" },
                    { key: "actions", title: "Acciones", align: "center" },
                  ]}
                >
                  {rows.map((row) => (
                    <tr key={row._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3.5 text-sm font-medium text-gray-900">
                        {row.name || productName(row.product)}
                        <span className="block text-xs text-gray-400">{productName(row.product)}</span>
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">{row.sourceLabel || "—"}</td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">{row.cava || "—"}</td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">
                        {fmtDate(row.entryDate)}
                        {row.daysInCava != null ? (
                          <span className="block text-xs text-gray-400">{row.daysInCava} días</span>
                        ) : null}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right">{fmt(row.cheeseCount, 0)}</td>
                      <td className="px-6 py-3.5 text-sm text-right">{fmt(row.queseraKg)} kg</td>
                      <td className="px-6 py-3.5 text-sm text-right">{fmt(row.cavaKg ?? row.initialWeight)} kg</td>
                      <td className="px-6 py-3.5 text-sm text-right font-semibold text-amber-800">
                        {fmt(row.salesKg)} kg
                        {row.salesWeekLabel ? (
                          <span className="block text-xs font-normal text-gray-400">
                            {row.salesWeekLabel}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right">{fmt(row.remainingKg)} kg</td>
                      <td className="px-6 py-3.5 text-center">
                        <Badge type={statusMeta(row.status).type}>
                          {row.statusLabel || statusMeta(row.status).label}
                        </Badge>
                      </td>
                      <td className="px-6 py-3.5 text-center">{actions(row)}</td>
                    </tr>
                  ))}
                </AppTable>
                {rows.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">
                    No hay lotes de maduración registrados
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <AppModalShell
        open={showEntry}
        onClose={resetEntry}
        title={editing ? "Editar carga a la cava" : "Cargar quesos a la cava"}
        widthClass="max-w-2xl"
        fitContent
        footer={
          <AppModalFooter>
            <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={resetEntry}>
              Cancelar
            </Button>
            <Button type="submit" form="maturation-entry-form" className="w-full sm:w-auto" disabled={saving}>
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </AppModalFooter>
        }
      >
        {entryFields}
      </AppModalShell>

      <AppModalShell
        open={showExit}
        onClose={resetExit}
        title="Registrar salida de cava"
        widthClass="max-w-md"
        fitContent
        footer={
          <AppModalFooter>
            <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={resetExit}>
              Cancelar
            </Button>
            <Button type="submit" form="maturation-exit-form" className="w-full sm:w-auto" disabled={saving}>
              {saving ? "Guardando…" : "Registrar salida"}
            </Button>
          </AppModalFooter>
        }
      >
        {exitFields}
      </AppModalShell>
    </>
  );
};

export default MaturationModule;
